import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Navbar from "./components/Navbar";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AcceptInvitePage from "./pages/AcceptInvitePage";
import AdminPage from "./pages/AdminPage";
import MatchingPage from "./pages/MatchingPage";
import FeedbackPage from "./pages/FeedbackPage";
import FeedbackFormPage from "./pages/FeedbackFormPage";
import ChiamaPazientePage from "./pages/ChiamaPazientePage";
import MonitorPage from "./pages/MonitorPage";
import MatchingAppuntamentiPage from "./pages/MatchingAppuntamentiPage";
import AppuntamentiDuplicatiPage from "./pages/AppuntamentiDuplicatiPage";
import PuliziaCalendarioPage from "./pages/PuliziaCalendarioPage";
import OpportunitaCreatePage from "./pages/OpportunitaCreatePage";
import ConfrontoOpportunitaPage from "./pages/ConfrontoOpportunitaPage";
import DashboardPage from "./pages/DashboardPage";
import PatientFrequencyPage from "./pages/PatientFrequencyPage";
import VideoTutorialPage from "./pages/VideoTutorialPage";
import StoricoStagePage from "./pages/StoricoStagePage";
import PromemoriaPage from "./pages/PromemoriaPage";
import TreatmentsCatalogPage from "./pages/TreatmentsCatalogPage";
import ConfrontoListiniPage from "./pages/ConfrontoListiniPage";
import PreventiviArchivioPage from "./pages/PreventiviArchivioPage";
import PreventivoChatPage from "./pages/PreventivoChatPage";
import IstruzioniAiPage from "./pages/IstruzioniAiPage";
import PianiCuraPage from "./pages/PianiCuraPage";
import ReportSalvataggiTemporaneiPage from "./pages/ReportSalvataggiTemporaneiPage";
import LogAttivitaPage from "./pages/LogAttivitaPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import IdleLogoutMonitor from "./components/IdleLogoutMonitor";
import ChatbotWidget from "./components/ChatbotWidget";
import GhlRateLimitBanner from "./components/GhlRateLimitBanner";
import { PwaInstallProvider } from "./contexts/PwaInstallContext";
import PianoCuraPage from "./App";
import { getPianoLock } from "./lib/piano-lock";

// Decides where "/" should land. Honour the session piano-lock if set
// (operatori vincolati a una specifica vista Piano di Cura), altrimenti
// la home del backoffice è "Chiama Paziente" (richiesta esplicita).
function PianoCuraRootRedirect() {
  const lock = getPianoLock();
  if (lock === "ortodonzia") return <Navigate to="/piano-cura-ortodonzia" replace />;
  if (lock === "clinico") return <Navigate to="/piano-cura-clinico" replace />;
  return <Navigate to="/chiama-paziente" replace />;
}

function AppRoutes() {
  const location = useLocation();

  // Detect session_id in hash synchronously — process BEFORE ProtectedRoute runs
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInvitePage />} />
      <Route path="/feedback-form" element={<FeedbackFormPage />} />
      <Route path="/monitor" element={
        <ProtectedRoute requiredPage="monitor">
          <MonitorPage />
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute adminOnly>
          <Navbar />
          <AdminPage />
        </ProtectedRoute>
      } />
      <Route path="/gestione-listino" element={
        <ProtectedRoute requiredFlag="manage_treatments">
          <Navbar />
          <TreatmentsCatalogPage />
        </ProtectedRoute>
      } />
      <Route path="/piani-cura" element={
        <ProtectedRoute requiredPage="piani-cura">
          <Navbar />
          <PianiCuraPage />
        </ProtectedRoute>
      } />
      <Route path="/matching" element={
        <ProtectedRoute requiredPage="matching">
          <Navbar />
          <MatchingPage />
        </ProtectedRoute>
      } />
      <Route path="/feedback" element={
        <ProtectedRoute requiredPage="feedback">
          <Navbar />
          <FeedbackPage />
        </ProtectedRoute>
      } />
      <Route path="/chiama-paziente" element={
        <ProtectedRoute requiredPage="chiama-paziente">
          <Navbar />
          <ChiamaPazientePage />
        </ProtectedRoute>
      } />
      <Route path="/matching-appuntamenti" element={
        <ProtectedRoute requiredPage="matching-appuntamenti">
          <Navbar />
          <MatchingAppuntamentiPage />
        </ProtectedRoute>
      } />
      <Route path="/appuntamenti-duplicati" element={
        <ProtectedRoute requiredPage="appuntamenti-duplicati">
          <Navbar />
          <AppuntamentiDuplicatiPage />
        </ProtectedRoute>
      } />
      <Route path="/pulizia-calendario" element={
        <ProtectedRoute adminOnly>
          <Navbar />
          <PuliziaCalendarioPage />
        </ProtectedRoute>
      } />
      <Route path="/salvataggi-temporanei" element={
        <ProtectedRoute requiredPage="piano-cura">
          <Navbar />
          <ReportSalvataggiTemporaneiPage />
        </ProtectedRoute>
      } />
      <Route path="/log-attivita" element={
        <ProtectedRoute adminOnly>
          <Navbar />
          <LogAttivitaPage />
        </ProtectedRoute>
      } />
      <Route path="/piano-cura-clinico" element={
        <ProtectedRoute requiredPage="piano-cura">
          <Navbar />
          <PianoCuraPage mode="clinico" />
        </ProtectedRoute>
      } />
      <Route path="/piano-cura-ortodonzia" element={
        <ProtectedRoute requiredPage="piano-cura">
          <Navbar />
          <PianoCuraPage mode="ortodonzia" />
        </ProtectedRoute>
      } />
      <Route path="/opportunita-create" element={
        <ProtectedRoute requiredPage="piano-cura">
          <Navbar />
          <OpportunitaCreatePage />
        </ProtectedRoute>
      } />
      <Route path="/preventivi" element={
        <ProtectedRoute requiredFlag="ai_assistant">
          <Navbar />
          <PreventiviArchivioPage />
        </ProtectedRoute>
      } />
      <Route path="/preventivo/nuovo/:opportunityId" element={
        <ProtectedRoute requiredFlag="ai_assistant">
          <Navbar />
          <PreventivoChatPage />
        </ProtectedRoute>
      } />
      <Route path="/istruzioni-ai" element={
        <ProtectedRoute adminOnly>
          <Navbar />
          <IstruzioniAiPage />
        </ProtectedRoute>
      } />
      <Route path="/knowledge-base" element={
        <ProtectedRoute requiredFlag="ai_assistant">
          <Navbar />
          <KnowledgeBasePage />
        </ProtectedRoute>
      } />
      <Route path="/confronto-opportunita" element={
        <ProtectedRoute requiredPage="confronto-opportunita">
          <Navbar />
          <ConfrontoOpportunitaPage />
        </ProtectedRoute>
      } />
      <Route path="/dashboard" element={
        <ProtectedRoute requiredPage="dashboard">
          <Navbar />
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/pazienti-ricorrenti" element={
        <ProtectedRoute requiredPage="pazienti-ricorrenti">
          <Navbar />
          <PatientFrequencyPage />
        </ProtectedRoute>
      } />
      <Route path="/confronto-listini" element={
        <ProtectedRoute requiredPage="confronto-listini">
          <Navbar />
          <ConfrontoListiniPage />
        </ProtectedRoute>
      } />
      <Route path="/video-tutorial" element={
        <ProtectedRoute requiredPage="video-tutorial">
          <Navbar />
          <VideoTutorialPage />
        </ProtectedRoute>
      } />
      <Route path="/storico-stage" element={
        <ProtectedRoute requiredPage="storico-stage">
          <Navbar />
          <StoricoStagePage />
        </ProtectedRoute>
      } />
      <Route path="/archivio-pratiche" element={
        <ProtectedRoute requiredPage="storico-stage">
          <Navbar />
          <StoricoStagePage mode="archivio" />
        </ProtectedRoute>
      } />
      <Route path="/promemoria-clinico" element={
        <ProtectedRoute requiredPage="chiama-paziente">
          <Navbar />
          <PromemoriaPage kind="clinico" />
        </ProtectedRoute>
      } />
      <Route path="/promemoria-ortodonzia" element={
        <ProtectedRoute requiredPage="chiama-paziente">
          <Navbar />
          <PromemoriaPage kind="orto" />
        </ProtectedRoute>
      } />
      {/* Root: redirect to the Reparto Clinico page (or to the locked page
          if the user has locked one of the two Piano di Cura views). */}
      <Route path="/" element={<PianoCuraRootRedirect />} />
    </Routes>
  );
}

export default function Root() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PwaInstallProvider>
          <ConditionalIdleLogout />
          <GhlRateLimitBanner />
          <ChatbotWidget />
          <AppRoutes />
        </PwaInstallProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// `IdleLogoutMonitor` traccia eventi mouse/tastiera per disconnettere
// dopo 30 min di inattività. La pagina `/monitor` è progettata per stare
// aperta sempre (sala d'attesa) — se applicassimo il timer di idle
// l'utente verrebbe sloggato e lo schermo mostrerebbe la login page.
// Quindi su `/monitor` saltiamo del tutto il monitor di idle.
function ConditionalIdleLogout() {
  const location = useLocation();
  if (location.pathname === "/monitor") return null;
  return <IdleLogoutMonitor />;
}
